import type { ApplicationInsights, ITelemetryItem } from '@microsoft/applicationinsights-web'

export type TelemetryConfig = {
  /** The App Insights connection string. Empty or missing leaves telemetry off. */
  connectionString?: string
  /** Shown as the cloud role, so browser errors sit beside the API's in App Insights. */
  roleName: string
}

export type Telemetry = {
  /** Logs to the console and, when telemetry is on, sends the error to App Insights. */
  captureError(error: unknown, properties?: Record<string, string>): void
  /** A pseudonymous id, such as the B2C subject, never an email. `null` on sign-out. */
  setUser(id: string | null): void
}

type ErrorInfo = { componentStack?: string }

type RootErrorHandlers = {
  onCaughtError(error: unknown, info: ErrorInfo): void
  onUncaughtError(error: unknown, info: ErrorInfo): void
}

// Query strings carry tokens (printToken, share links) and the MSAL redirect puts the auth
// code in the hash, so neither may reach App Insights. Parameter names are kept for context.
function scrubUrl(value: string) {
  return value.replace(/([?&][^=&#\s]+)=[^&#\s]*/g, '$1=REDACTED').replace(/#\S*/g, '')
}

const URL_FIELDS = ['uri', 'refUri', 'target', 'name', 'data'] as const

function scrub(item: ITelemetryItem) {
  const baseData = item.baseData
  if (baseData) {
    for (const field of URL_FIELDS) {
      const value = baseData[field]
      if (typeof value === 'string') baseData[field] = scrubUrl(value)
    }
  }
  const trace = item.ext?.trace
  if (typeof trace?.name === 'string') trace.name = scrubUrl(trace.name)
}

function toError(error: unknown) {
  if (error instanceof Error) return error
  if (typeof error === 'string') return new Error(error)
  try {
    return new Error(JSON.stringify(error))
  } catch {
    return new Error(String(error))
  }
}

/**
 * Browser error reporting to Application Insights. The SDK is loaded on first use in its own
 * chunk, so it stays off the critical path; calls made before it arrives are queued. Besides
 * `captureError`, the SDK reports uncaught errors and unhandled rejections by itself, and
 * tracks `/api` calls so a browser error links to the API request that caused it.
 */
export function createTelemetry({ connectionString, roleName }: TelemetryConfig): Telemetry {
  let sdk: ApplicationInsights | null = null
  let pending: ((ai: ApplicationInsights) => void)[] | null = connectionString ? [] : null

  function withSdk(fn: (ai: ApplicationInsights) => void) {
    if (sdk) {
      try {
        fn(sdk)
      } catch {
        // Reporting an error must never cause one.
      }
    } else {
      pending?.push(fn)
    }
  }

  if (connectionString) {
    import('@microsoft/applicationinsights-web')
      .then(({ ApplicationInsights }) => {
        const ai = new ApplicationInsights({
          config: { connectionString, enableUnhandledPromiseRejectionTracking: true },
        })
        ai.loadAppInsights()
        ai.addTelemetryInitializer((item) => {
          item.tags = { ...item.tags, 'ai.cloud.role': roleName }
          scrub(item)
        })
        sdk = ai
        for (const fn of pending ?? []) withSdk(fn)
        pending = null
      })
      .catch(() => {
        // Usually a content blocker. Stop queueing so the queue can't grow for the session.
        pending = null
      })
  }

  return {
    captureError(error, properties) {
      // React stops logging caught errors itself once a root has an onCaughtError handler,
      // so without this a developer would never see them.
      console.error(error)
      withSdk((ai) => ai.trackException({ exception: toError(error), properties }))
    },

    setUser(id) {
      withSdk((ai) => {
        if (id) ai.setAuthenticatedUserContext(id, undefined, false)
        else ai.clearAuthenticatedUserContext()
      })
    },
  }
}

/**
 * Error handlers for React 19's `createRoot` options. Every error a component throws passes
 * through one of these, including those a route's `errorElement` catches.
 */
export function rootErrorHandlers(telemetry: Telemetry): RootErrorHandlers {
  const report = (handled: string) => (error: unknown, info: ErrorInfo) =>
    telemetry.captureError(error, { handled, componentStack: info.componentStack ?? '' })

  return {
    onCaughtError: report('error-boundary'),
    onUncaughtError: report('uncaught'),
  }
}
